import React, {
  ChangeEvent,
  ReactNode,
  forwardRef,
  ReactElement,
  Ref,
  useState,
} from "react";
import { TransitionProps } from "@mui/material/transitions";
import {
  Button,
  Dialog,
  Slide,
  Stack,
  Box,
  Card,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { mobileCardSx, cardHeaderSx, cardSx } from "utils";
import { useTranslation } from "react-i18next";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import { DisplayMode, EntityTypes, Permit, PermitStatus } from "types";
import { useFetcher } from "react-router";
import { PermitTableActionHandler } from "./PermitTable";
import { ValidatorForm } from "react-material-ui-form-validator";
import AuturaTextField from "components/inputs/AuturaTextField";
import { ROUTES } from "router/routes";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});
interface RevokePermitDialogProps {
  open: boolean;
  handleCancel: () => void;
  permit: Permit;
  displayMode: DisplayMode;
  fetcherKey: string;
}

const PermitRevokeDialog = ({
  open,
  handleCancel,
  permit,
  fetcherKey,
  displayMode,
}: RevokePermitDialogProps): ReactNode => {
  const { t } = useTranslation();
  const fetcher = useFetcher({ key: fetcherKey });
  const { palette, breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down("lg"));
  const [reason, setReason] = useState("");
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  const handleSubmit = () => {
    const updatedPermit: Permit = {
      ...permit,
      permitStatus: PermitStatus.Disabled,
      data: { ...permit.data, cancelReason: reason },
    };
    if (permit.vehicle && permit.vehicle.year) {
      updatedPermit.vehicle.year = parseInt(`${permit.vehicle.year}`, 10);
    }
    fetcher.submit(
      {
        body: JSON.stringify(updatedPermit),
        handler: PermitTableActionHandler.REVOKE_PERMIT,
        entityType:
          displayMode === "property"
            ? EntityTypes.Property
            : EntityTypes.PropertyGroup,
        tenantID: permit?.tenant.id || 0,
        permitID: permit?.id || 0,
        entityID:
          displayMode === "property"
            ? permit?.property?.id || 0
            : permit?.propertyGroup?.id || 0,
      },
      {
        method: "PUT",
        action: ROUTES.PROPERTY_PERMITS,
        encType: "application/json",
      },
    );
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      aria-label={`${t("dialogs.revoke.title", { ns: "permits" })} Dialog`}
      fullScreen={mobile}
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
    >
      <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
        <Card sx={mobile ? mobileCardSx : cardSx}>
          <Box
            sx={
              mobile
                ? {
                    display: "flex",
                    p: { xs: 2, sm: 4 },
                    backgroundColor: palette.secondary.main,
                  }
                : cardHeaderSx
            }
          >
            <Box sx={{ flex: 1, p: 0.5 }}>
              <Typography
                variant={"h4"}
                sx={{
                  color: palette.secondary.contrastText,
                  textAlign: "left",
                }}
              >
                {t("dialogs.revoke.title", { ns: "permits" })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <Grid container width={"100%"} spacing={1.5}>
              <Grid size={12}>
                <AuturaTextField
                  autoFocus
                  required
                  label={t("dialogs.revoke.reason", { ns: "permits" })}
                  name="permit.data.cancelReason"
                  value={reason}
                  type="text"
                  fullWidth
                  onChange={handleChange}
                />
              </Grid>
              <Grid size={12}>
                <Stack
                  width="100%"
                  direction="row"
                  spacing={2}
                  justifyContent="flex-end"
                  mt={2}
                  display="flex"
                  alignItems="flex-end"
                  alignContent="flex-end"
                >
                  <Button
                    variant={"outlined"}
                    onClick={handleCancel}
                    color={"secondary"}
                    endIcon={<CancelIcon />}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    variant={"contained"}
                    type="submit"
                    color={"secondary"}
                    endIcon={<WarningIcon />}
                    disabled={!reason}
                  >
                    {t("dialogs.revoke.button", { ns: "permits" })}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </ValidatorForm>
    </Dialog>
  );
};
export default PermitRevokeDialog;
